import styled, { css } from 'styled-components'
import type { FunctionComponent, ReactNode, MouseEventHandler } from 'react'

export enum Variant {
  Blank = 'blank',
  Primary = 'primary',
  PrimaryInverted = 'primaryInverted',
  Tertiary = 'tertiary',
  TextButton = 'textButton',
  Menu = 'menu',
  Hidden = 'hidden',
}

const StyledButton = styled.button<{
  // adding a size leads to a square-formed button
  $size?: number
  $variant?: Variant
}>`
  text-decoration: none;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  line-height: 1.375;
  font-weight: 700;
  padding: ${(props) => (props.$size ? '0' : '0.75rem 1rem')};
  min-width: ${(props) => (props.$size ? `${props.$size}px` : '90px')};
  width: ${(props) => (props.$size ? `${props.$size}px` : 'auto')};
  height: ${(props) => (props.$size ? `${props.$size}px` : '2.75rem')};
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }

  ${({ $variant }) => {
    switch ($variant) {
      case Variant.Primary:
      case undefined:
        return css`
          background-color: var(--blue);
          color: var(--light);
          &:focus,
          &:hover {
            background-color: var(--blueDark);
          }
          svg {
            fill: var(--light);
          }
        `
      case Variant.PrimaryInverted:
        return css`
          background-color: var(--light);
          color: var(--blue);
          border: 1px solid var(--blue);
          &:focus,
          &:hover {
            background-color: var(--greySuperLight);
          }
          svg,
          rect {
            fill: var(--blue);
            path {
              fill: var(--blue);
            }
          }
        `
      case Variant.Blank:
        return css`
          background-color: var(--light);
          color: var(--dark);
          &:hover {
            background-color: var(--greyLight);
          }
          svg,
          svg path {
            fill: var(--dark);
          }
        `
      case Variant.TextButton:
        return css`
          height: auto;
          min-width: auto;
          padding: 0;
          align-self: baseline;
          white-space: normal;
          justify-content: left;
          text-align: left;
          color: var(--blue);
          background-color: transparent;
          svg,
          path {
            fill: var(--blue);
          }
          &:hover {
            color: var(--red);
            svg,
            path {
              fill: var(--red);
            }
          }
          margin-right: 1rem;
        `
      case Variant.Tertiary:
        return css`
          background-color: var(--greyMedium);
          svg {
            fill: var(--dark);
          }
          &:focus,
          &:hover {
            background-color: var(--greyText);
          }
        `
      case Variant.Menu:
        return css`
          padding: 0;
          min-width: auto;
          font-weight: 700;
          font-size: 1.125rem;
          line-height: 1.6;
          color: var(--blue);
          background-color: inherit;
          height: 100%;
          &:hover,
          &:focus {
            text-decoration: underline;
            text-decoration-thickness: 2px;
            text-underline-offset: 0.25rem;
            color: var(--blue);
            svg path {
              fill: var(--blue);
            }
          }

          @media screen and (min-width: 48rem) {
            padding: 1rem 1rem 1rem 2rem;
            color: var(--dark);
            font-weight: 400;

            &:hover,
            &:focus {
              text-decoration: none;
              color: var(--blue);
            }
          }
        `
      case Variant.Hidden:
        return css`
          background: transparent;
          padding: 0;
          margin-bottom: 0.75rem;
          min-width: 0;
          @media screen and (min-width: 48rem) {
            margin-bottom: 0;
          }
        `
      default:
        return null
    }
  }}

  svg {
    margin-right: ${(props) => (props.$size ? '0' : '0.675rem')};
  }
  &:disabled {
    background-color: var(--greyLight);
    svg {
      fill: var(--greyMedium);
    }
  }
`

interface ButtonProps {
  children?: ReactNode
  className?: string
  disabled?: boolean
  id?: string
  onClick?: MouseEventHandler
  size?: number
  title?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: Variant
}
const Button: FunctionComponent<ButtonProps> = ({
  children,
  className,
  disabled,
  id,
  onClick,
  size,
  title,
  type,
  variant,
  ...otherProps
}) => (
  <StyledButton
    className={className}
    disabled={disabled}
    id={id}
    onClick={onClick}
    $size={size}
    title={title}
    type={type}
    $variant={variant}
    {...otherProps}
  >
    {children}
  </StyledButton>
)
export default Button
